import { FC, useRef, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Popover, PopoverPortalContent } from "@/components/_atoms/popover";
import { PopoverTrigger } from "@/components/_atoms/popover";
import Datepicker from "@/components/_molecules/datepicker/datepicker";
import { cn } from "@/lib/cn";
import { getDateFnsLocale } from "@/lib/date-fns-locale";

import { DateClearControl } from "./date-clear-control";

export interface InputDateProps {
  onValueChange?: (value: string | null) => void;
  className?: string;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  label?: string;
  iconPosition?: "left" | "right";
  "aria-label"?: string;
  "aria-describedby"?: string;
}

export const InputDate: FC<InputDateProps> = ({
  onValueChange,
  defaultValue,
  disabled,
  placeholder,
  minDate,
  maxDate,
  className,
  id,
  label,
  iconPosition = "right",
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
}) => {
  const { t } = useTranslation("common");
  const [value, setValue] = useState<string | null>(defaultValue ? defaultValue : null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleDateSelected = (selectedDate: Date | null) => {
    if (!selectedDate) {
      setValue(null);
      if (onValueChange) {
        onValueChange(null);
      }
      return;
    }

    const dateString = selectedDate.toISOString();
    setValue(dateString);
    setIsCalendarOpen(false);

    if (onValueChange) {
      onValueChange(dateString);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (buttonRef.current && !disabled) {
          buttonRef.current.click();
        }
        break;
    }
  };

  const formatDisplayDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "PPP", { locale: getDateFnsLocale() });
    } catch {
      return dateString;
    }
  };

  const displayValue = value ? formatDisplayDate(value) : placeholder || t("datepicker.selectDatePlaceholder");

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setValue(null);
    if (onValueChange) {
      onValueChange(null);
    }
  };

  const isClearVisible = Boolean(value) && !disabled;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="mb-2 block text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <Popover
        open={isCalendarOpen}
        onOpenChange={(open) => {
          if (!disabled) {
            setIsCalendarOpen(open);
          }
        }}
      >
        <div className="relative w-full">
          <PopoverTrigger asChild>
            <Button
              ref={buttonRef}
              size="sm"
              id={id}
              // aria-disabled keeps the field in the tab order in read mode
              aria-disabled={disabled || undefined}
              onClick={(event) => {
                if (disabled) {
                  event.preventDefault();
                }
              }}
              variant="ghost"
              tabIndex={0}
              className={cn([
                "w-full min-w-0 border pl-3 text-left font-normal focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
                isClearVisible && "pr-10",
                className ?? "",
              ])}
              onKeyDown={handleKeyDown}
              aria-haspopup="dialog"
              aria-label={ariaLabel || label || t("datepicker.datePicker")}
              aria-describedby={ariaDescribedBy}
              role="combobox"
              aria-autocomplete="none"
            >
              {iconPosition === "left" && (
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
              <span
                className={cn([
                  "min-w-0 flex-1 truncate text-left",
                  iconPosition === "right" && "mr-2",
                  value && disabled ? "text-foreground" : "text-muted-foreground",
                ])}
              >
                {displayValue}
              </span>
              {iconPosition === "right" && (!value || disabled) && (
                <CalendarIcon className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
            </Button>
          </PopoverTrigger>
          {isClearVisible && <DateClearControl onClear={handleClear} label={t("datepicker.clearDate")} size="sm" />}
        </div>
        <PopoverPortalContent
          className="w-auto border-0 bg-transparent p-0 shadow-none"
          align="start"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => buttonRef.current?.focus(), 0);
          }}
        >
          <div role="dialog" aria-modal="true" aria-label={t("datepicker.datePicker")}>
            <Datepicker
              onValueChange={handleDateSelected}
              defaultValue={value ? new Date(value) : undefined}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </PopoverPortalContent>
      </Popover>
    </div>
  );
};
